<template>
  <div class="clinical-assessment-page">
    <header class="assessment-header">
      <div class="header-copy">
        <h1>Facial Assessment</h1>

        <div v-if="subject" class="case-meta">
          <span class="case-number"> Case {{ currentSubjectIndex + 1 }} </span>

          <span class="ethnicity-badge">
            Ethnicity: {{ ethnicityLabel }}
          </span>
        </div>
      </div>

      <div class="progress-badge">
        <span class="progress-label"> CASE </span>
        <span>
          {{ currentSubjectIndex + 1 }}
        </span>
        <span v-if="totalSubjects"> / {{ totalSubjects }} </span>
      </div>
    </header>
    <div v-if="allCasesCompleted" class="completion-message">
      <div class="completion-content">
        <span class="completion-icon"> ✓ </span>

        <div class="completion-copy">
          <strong> Assessment complete </strong>

          <span>
            You have completed all {{ totalSubjects }} available cases.
          </span>
        </div>
      </div>

      <button type="button" class="review-button" @click="reviewFromBeginning">
        Review from Case 1
      </button>
    </div>


    <div v-if="errorMessage" class="error-message">
      <div class="error-content">
        <span class="error-icon"> ! </span>

        <span>
          {{ errorMessage }}
        </span>
      </div>

      <button
        type="button"
        class="retry-button"
        @click="loadSubject(currentSubjectId)"
      >
        Retry
      </button>
    </div>

    <main
      v-show="!errorMessage && !allCasesCompleted"
      class="assessment-workspace"
    >
      
      <section class="viewer-card">
        <div class="viewer-panel">
          <div class="panel-heading">
            <div>
              <span class="panel-eyebrow"> SUBJECT </span>

              <h2>3D Facial View</h2>
            </div>

            <span class="viewer-instruction">
              Drag to rotate · Scroll to zoom
            </span>
          </div>

          <div class="viewer-stage">
            <!-- Three.js mounts here -->
            <div ref="viewerContainer" class="three-viewer"></div>

            <!-- Loading overlay -->
            <div v-if="loading" class="viewer-loading">
              <div class="spinner"></div>

              <span>
                Loading case
                {{ currentSubjectIndex + 1 }}...
              </span>
            </div>
          </div>
        </div>

        <aside class="reference-panel">
        <div class="reference-header">
          <span class="panel-eyebrow"> REFERENCE </span>

          <h2>Lip / Philtrum Guide</h2>

          <!-- Normal case: one guide automatically selected -->
          <span
            v-if="!showReferenceSelector"
            class="reference-ethnicity"
          >
            {{
              selectedReferenceGuide === "african"
                ? "African-American"
                : "White"
            }}
          </span>

          <!-- Mixed / Unknown / Other -->
          <div
            v-else
            class="reference-guide-selector"
          >
            <button
              type="button"
              class="reference-guide-button"
              :class="{
                active: selectedReferenceGuide === 'white',
              }"
              @click="selectedReferenceGuide = 'white'"
            >
              White
            </button>

            <button
              type="button"
              class="reference-guide-button"
              :class="{
                active: selectedReferenceGuide === 'african',
              }"
              @click="selectedReferenceGuide = 'african'"
            >
              African-American
            </button>
          </div>
        </div>

          <div class="reference-body">
            <div v-if="referenceImage" class="reference-image-frame">
              <img
                :src="referenceImage"
                class="reference-image"
                alt="Lip and philtrum reference scale"
              />
            </div>

            <div v-else class="reference-unavailable">
              <span class="reference-placeholder-icon"> ! </span>

              <span> Reference guide unavailable </span>
            </div>
          </div>
        </aside>
      </section>
      <section class="scoring-card">
        <!-- Scoring header -->
        <div class="scoring-header">
          <div>
            <span class="section-eyebrow"> ASSESSMENT </span>

            <h2>Clinical scoring</h2>
          </div>

          <span class="scoring-help"> Select one score for each feature </span>
        </div>
        <div class="score-row">
          <div class="score-description">
            <span class="score-title"> Lip-Vermilion </span>

            <span class="score-subtitle"> Select a score from 1 to 5 </span>
          </div>

          <!-- Score buttons -->
          <div class="score-buttons" :class="{ disabled: lipNA }">
            <button
              v-for="score in scores"
              :key="'lip-' + score"
              type="button"
              class="score-button"
              :class="{
                selected: lipScore === score,
              }"
              :disabled="lipNA || saving"
              @click="selectLipScore(score)"
            >
              <span class="score-number">
                {{ score }}
              </span>
            </button>
          </div>

          <!-- N/A -->
          <label class="na-control" :class="{ active: lipNA }">
            <input
              v-model="lipNA"
              type="checkbox"
              :disabled="saving"
              @change="onLipNAChange"
            />

            <span class="custom-checkbox">
              <span v-if="lipNA"> ✓ </span>
            </span>

            <span class="na-copy">
              <strong> Can't assign </strong>

              <small> N/A </small>
            </span>
          </label>
        </div>

        <div class="score-divider"></div>
        <div class="score-row">
          <div class="score-description">
            <span class="score-title"> Philtrum </span>

            <span class="score-subtitle"> Select a score from 1 to 5 </span>
          </div>

          <!-- Score buttons -->
          <div class="score-buttons" :class="{ disabled: philtrumNA }">
            <button
              v-for="score in scores"
              :key="'philtrum-' + score"
              type="button"
              class="score-button"
              :class="{
                selected: philtrumScore === score,
              }"
              :disabled="philtrumNA || saving"
              @click="selectPhiltrumScore(score)"
            >
              <span class="score-number">
                {{ score }}
              </span>
            </button>
          </div>

          <!-- N/A -->
          <label class="na-control" :class="{ active: philtrumNA }">
            <input
              v-model="philtrumNA"
              type="checkbox"
              :disabled="saving"
              @change="onPhiltrumNAChange"
            />

            <span class="custom-checkbox">
              <span v-if="philtrumNA"> ✓ </span>
            </span>

            <span class="na-copy">
              <strong> Can't assign </strong>

              <small> N/A </small>
            </span>
          </label>
        </div>
        <div class="comments-section">
          <div class="comments-heading">
            <label for="assessment-comments"> Comments </label>

            <span> Optional </span>
          </div>

          <textarea
            id="assessment-comments"
            v-model="comments"
            rows="3"
            :disabled="saving"
            placeholder="Add any notes about image quality, expression or scoring..."
          ></textarea>
        </div>

        <div class="assessment-footer">
          <!-- Back -->
          <button
            type="button"
            class="navigation-button back-button"
            :disabled="currentSubjectIndex <= 0 || saving || loading"
            @click="previousSubject"
          >
            <span class="nav-arrow"> ← </span>

            <span> Back </span>
          </button>
          <div class="save-state">
            <span v-if="saving" class="saving-status">
              <span class="mini-spinner"></span>

              Saving assessment...
            </span>

            <span v-else-if="saved" class="saved-status">
              <span class="saved-check"> ✓ </span>

              Assessment saved
            </span>
          </div>
          <button
            type="button"
            class="navigation-button next-button"
            :disabled="saving || loading"
            @click="nextSubject"
          >
            <span> Save & Next </span>
            <span class="nav-arrow"> → </span>
          </button>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { markRaw } from "vue";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

export default {
  name: "ClinicalAssessmentComponent",

  data() {
    return {
      currentSubjectId: null,
      currentSubjectIndex: 0,
      subjectIds: [],
      // Subjects this user has already scored (null = unknown, always check the backend)
      assessedSubjectIds: null,
      // Form values as last loaded from / saved to the backend, to detect unsaved changes
      savedAssessmentState: null,
      totalSubjects: 0,
      subject: null,
      scores: [1, 2, 3, 4, 5],
      lipScore: null,
      philtrumScore: null,
      lipNA: false,
      philtrumNA: false,
      comments: "",
      loading: false,
      saving: false,
      saved: false,
      errorMessage: "",
      allCasesCompleted: false,
      scene: null,
      camera: null,
      renderer: null,
      controls: null,
      model: null,
      resizeObserver: null,
      animationFrameId: null,
      textureBlobUrl: null,
      modelBlobUrl: null,
      selectedReferenceGuide: "white",
    };
  },

  computed: {
  ethnicityLabel() {
    if (!this.subject) {
      return "";
    }

    return this.subject.ethnicity || "";
  },

  referenceGuideType() {
    if (!this.subject || !this.subject.ethnicity) {
      return "ambiguous";
    }

    const ethnicity = this.subject.ethnicity
      .toLowerCase()
      .trim();

    if (
      ethnicity.includes("african") ||
      ethnicity.includes("black")
    ) {
      return "african";
    }

    if (
      ethnicity.includes("caucasian") ||
      ethnicity.includes("white")
    ) {
      return "white";
    }

    // Mixed, unknown, other, etc.
    return "ambiguous";
  },

  showReferenceSelector() {
    return this.referenceGuideType === "ambiguous";
  },

  referenceImage() {
    if (this.selectedReferenceGuide === "african") {
      return require("@/assets/lipscaleAA.png");
    }

    return require("@/assets/lipscaleW.png");
  },
},

async mounted() {
  this.initializeThreeViewer();

  await this.assessmentProgress();
},

  beforeUnmount() {
    this.destroyThreeViewer();
    this.revokeBlobUrls();
  },

  methods: {
    async loadSubject(subjectId) {
      this.loading = true;
      this.errorMessage = "";
      this.saved = false;
      let savedAssessmentRequest = null;

      try {
        const fasdBackendURL =
          localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
        const token = localStorage.getItem("userJWtoken");
        const subjectURL = `${fasdBackendURL}/api/clinicalAssesment/subjects/${subjectId}`;
        const subjectResponse = await fetch(subjectURL, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        });

        if (!subjectResponse.ok) {
          throw new Error(
            `Failed to load subject. Status: ${subjectResponse.status}`,
          );
        }

        this.subject = await subjectResponse.json();
        this.resetAssessment();
        this.savedAssessmentState = this.currentAssessmentState();
        // Load this user's saved selection while the 2D/3D files download
        savedAssessmentRequest = this.loadSavedAssessment(
          subjectId,
          fasdBackendURL,
          token,
        );
        if (this.referenceGuideType === "african") {
          this.selectedReferenceGuide = "african";
        } else {
          // White subjects and ambiguous subjects default to White.
          this.selectedReferenceGuide = "white";
        }

        const imageURL = `${fasdBackendURL}/api/clinicalAssesment/subjects/${subjectId}/2d`;

        const modelURL = `${fasdBackendURL}/api/clinicalAssesment/subjects/${subjectId}/3d`;
        const [imageResponse, modelResponse] = await Promise.all([
          fetch(imageURL, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/octet-stream",
            },
          }),

          fetch(modelURL, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/octet-stream",
            },
          }),
        ]);

        if (!imageResponse.ok) {
          throw new Error(
            `Failed to load 2D image. Status: ${imageResponse.status}`,
          );
        }

        if (!modelResponse.ok) {
          throw new Error(
            `Failed to load 3D model. Status: ${modelResponse.status}`,
          );
        }
        const imageBlob = await imageResponse.blob();
        const modelBlob = await modelResponse.blob();

        console.log(
          "2D size:",
          (imageBlob.size / 1024 / 1024).toFixed(2),
          "MB",
        );

        console.log(
          "3D size:",
          (modelBlob.size / 1024 / 1024).toFixed(2),
          "MB",
        );
        this.revokeBlobUrls();

            this.textureBlobUrl = URL.createObjectURL(imageBlob);
            this.modelBlobUrl = URL.createObjectURL(modelBlob);

            await this.loadThreeModel(
              this.modelBlobUrl,
              this.textureBlobUrl,
            );
        console.log(`Subject ${subjectId} loaded successfully`);
      } catch (error) {
        console.error("Clinical assessment error:", error);
        this.errorMessage =
          error.message || "Unable to load clinical assessment.";
      } finally {
        if (savedAssessmentRequest) {
          await savedAssessmentRequest;
        }
        this.loading = false;
      }
    },
    // Fills the form with this user's saved assessment for the subject, if there is one.
    async loadSavedAssessment(subjectId, fasdBackendURL, token) {
      if (
        this.assessedSubjectIds &&
        !this.assessedSubjectIds.includes(subjectId)
      ) {
        return; // not scored yet, nothing to load
      }

      const blankState = this.currentAssessmentState();

      try {
        const response = await fetch(
          `${fasdBackendURL}/api/clinicalAssesment/subjects/${subjectId}/assessment`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          },
        );

        if (response.status === 404) {
          return; // not scored yet
        }

        if (!response.ok) {
          throw new Error(
            `Failed to load saved assessment. Status: ${response.status}`,
          );
        }

        const assessment = await response.json();

        // Ignore a late response if the user has moved on or already started scoring
        if (
          subjectId !== this.currentSubjectId ||
          this.currentAssessmentState() !== blankState
        ) {
          return;
        }

        this.lipNA = Boolean(assessment.lip_na);
        this.philtrumNA = Boolean(assessment.philtrum_na);
        this.lipScore = this.lipNA ? null : assessment.lip_score;
        this.philtrumScore = this.philtrumNA ? null : assessment.philtrum_score;
        this.comments = assessment.comments || "";

        this.savedAssessmentState = this.currentAssessmentState();
        this.saved = true;
      } catch (error) {
        console.error("Saved assessment error:", error);

        if (subjectId === this.currentSubjectId) {
          this.errorMessage = "Unable to load your saved selection for this case.";
        }
      }
    },
    async reviewFromBeginning() {
      if (!this.subjectIds.length) {
        return;
      }

      this.allCasesCompleted = false;
      this.errorMessage = "";

      this.currentSubjectIndex = 0;
      this.currentSubjectId = this.subjectIds[0];

      await this.loadSubject(this.currentSubjectId);
    },
    async assessmentProgress() {
      this.allCasesCompleted = false;
      try {
        const fasdBackendURL =
          localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
        const token = localStorage.getItem("userJWtoken");
        const response = await fetch(
          `${fasdBackendURL}/api/clinicalAssesment/subjects-count`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          },
        );

        if (!response.ok) {
          throw new Error(`Failed to get subjects. Status: ${response.status}`);
        }
        const data = await response.json();
        this.totalSubjects = data.total;
        this.subjectIds = data.subject_ids || [];
        this.assessedSubjectIds = data.assessed_subject_ids || null;
        this.completedSubjects = data.completed || 0;
        if (this.subjectIds.length === 0) {
          this.errorMessage = "No subjects are available.";
          return;
        }
        if (data.next_subject_id === null) {
          this.allCasesCompleted = true;
          this.errorMessage = "";

          return;
        }

        this.currentSubjectId = data.next_subject_id;

        this.currentSubjectIndex = this.subjectIds.indexOf(
          this.currentSubjectId,
        );

        await this.loadSubject(this.currentSubjectId);
      } catch (error) {
        console.error("Failed to load subjects:", error);

        this.errorMessage = error.message || "Unable to load subjects.";
      }
    },
    initializeThreeViewer() {
      const container = this.$refs.viewerContainer;
      if (!container) {
        console.error("3D viewer container not found.");
        return;
      }

      const width = container.clientWidth || 800;
      const height = container.clientHeight || 500;
      this.scene = markRaw(new THREE.Scene());
      this.scene.background = new THREE.Color(0xf4f6f8);
      this.camera = markRaw(
        new THREE.PerspectiveCamera(35, width / height, 0.01, 10000),
      );

      this.camera.position.set(0, 0, 5);
      this.renderer = markRaw(
        new THREE.WebGLRenderer({
          antialias: true,
          alpha: false,
        }),
      );
      this.renderer.outputColorSpace = THREE.SRGBColorSpace;
      this.renderer.toneMapping = THREE.NoToneMapping;
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
      this.renderer.setSize(width, height);
      container.appendChild(this.renderer.domElement);
      this.controls = markRaw(
        new OrbitControls(this.camera, this.renderer.domElement),
      );

      this.controls.enableDamping = true;
      this.controls.dampingFactor = 0.08;
      this.controls.enableZoom = true;
      this.controls.enableRotate = true;
      this.controls.enablePan = true;

      const ambientLight = new THREE.AmbientLight(0xffffff, 0.9);
      this.scene.add(ambientLight);
      const frontLight = new THREE.DirectionalLight(0xffffff, 0.9);
      frontLight.position.set(0, 2, 5);
      this.scene.add(frontLight);
      const leftLight = new THREE.DirectionalLight(0xffffff, 0.4);
      leftLight.position.set(-5, 1, 3);
      this.scene.add(leftLight);
      const rightLight = new THREE.DirectionalLight(0xffffff, 0.4);
      rightLight.position.set(5, 1, 3);
      this.scene.add(rightLight);
      this.resizeObserver = new ResizeObserver(() => {
        this.resizeViewer();
      });

      this.resizeObserver.observe(container);

      this.animate();
    },
    loadThreeModel(modelUrl, textureUrl) {
  return new Promise((resolve, reject) => {
    const textureLoader = new THREE.TextureLoader();

    textureLoader.load(
      textureUrl,

      (loadedTexture) => {
        const texture = markRaw(loadedTexture);

        /*
         * IMPORTANT:
         * Your old OBJ implementation used flipY=true.
         *
         * glTF uses a different texture-coordinate convention,
         * so start with false for GLB.
         */
        texture.flipY = false;

        texture.colorSpace = THREE.SRGBColorSpace;
        texture.needsUpdate = true;

        const gltfLoader = new GLTFLoader();

        gltfLoader.load(
          modelUrl,

          (gltf) => {
            this.removeCurrentModel();

            const object = markRaw(gltf.scene);

            object.traverse((child) => {
              if (!child.isMesh) {
                return;
              }

              const material = markRaw(
                new THREE.MeshBasicMaterial({
                  map: texture,
                  side: THREE.DoubleSide,
                }),
              );

              child.material = material;
              child.material.needsUpdate = true;
            });

            this.model = object;

            this.scene.add(
              this.model,
            );

            this.fitModelToViewer(
              this.model,
            );

            resolve();
          },

          undefined,

          (error) => {
            console.error(
              "GLB loading error:",
              error,
            );

            reject(
              new Error(
                "Unable to load the 3D model.",
              ),
            );
          },
        );
      },

      undefined,

      (error) => {
        console.error(
          "Texture loading error:",
          error,
        );

        reject(
          new Error(
            "Unable to load the 2D texture.",
          ),
        );
      },
    );
  });
},
    fitModelToViewer(object) {
      const box = new THREE.Box3().setFromObject(object);

      if (box.isEmpty()) {
        console.error("3D model has an empty bounding box.");

        return;
      }

      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      object.position.x -= center.x;
      object.position.y -= center.y;
      object.position.z -= center.z;

      const maxDimension = Math.max(size.x, size.y, size.z);
      const fovRadians = this.camera.fov * (Math.PI / 180);
      let cameraDistance = maxDimension / (2 * Math.tan(fovRadians / 2));
      cameraDistance *= 1.4;

      this.camera.position.set(0, 0, cameraDistance);
      this.camera.near = Math.max(cameraDistance / 100, 0.01);
      this.camera.far = Math.max(cameraDistance * 100, 1000);
      this.camera.updateProjectionMatrix();
      this.controls.target.set(0, 0, 0);
      this.controls.update();
    },

    animate() {
      this.animationFrameId = requestAnimationFrame(this.animate);

      if (this.controls) {
        this.controls.update();
      }

      if (this.renderer && this.scene && this.camera) {
        this.renderer.render(this.scene, this.camera);
      }
    },

    resizeViewer() {
      const container = this.$refs.viewerContainer;
      if (!container || !this.renderer || !this.camera) {
        return;
      }
      const width = container.clientWidth;
      const height = Math.max(container.clientHeight, 1);
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(width, height);
    },

    selectLipScore(score) {
      this.lipScore = score;
      this.lipNA = false;
      this.saved = false;
    },

    selectPhiltrumScore(score) {
      this.philtrumScore = score;
      this.philtrumNA = false;
      this.saved = false;
    },

    onLipNAChange() {
      if (this.lipNA) {
        this.lipScore = null;
      }

      this.saved = false;
    },

    onPhiltrumNAChange() {
      if (this.philtrumNA) {
        this.philtrumScore = null;
      }

      this.saved = false;
    },

    resetAssessment() {
      this.lipScore = null;
      this.philtrumScore = null;

      this.lipNA = false;
      this.philtrumNA = false;

      this.comments = "";
    },

    // The form values in the shape the backend saves them
    assessmentPayload() {
      return {
        lip_score: this.lipNA ? null : this.lipScore,
        philtrum_score: this.philtrumNA ? null : this.philtrumScore,
        lip_na: this.lipNA,
        philtrum_na: this.philtrumNA,
        comments: this.comments || null,
      };
    },

    currentAssessmentState() {
      return JSON.stringify(this.assessmentPayload());
    },

    hasUnsavedChanges() {
      return this.currentAssessmentState() !== this.savedAssessmentState;
    },

    isAssessed(subjectId) {
      return Boolean(
        this.assessedSubjectIds && this.assessedSubjectIds.includes(subjectId),
      );
    },

    async saveAssessment() {
      this.saving = true;
      this.saved = false;
      this.errorMessage = "";

      try {
        const fasdBackendURL =
          localStorage.getItem("fasdBackendURL") || "http://localhost:8000";
        const token = localStorage.getItem("userJWtoken");
        const url = `${fasdBackendURL}/api/clinicalAssesment/subjects/${this.currentSubjectId}/assessment`;
        const assessmentData = this.assessmentPayload();

        const response = await fetch(url, {
          method: "PUT",

          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },

          body: JSON.stringify(assessmentData),
        });

        if (!response.ok) {
          let errorMessage = `Failed to save assessment. Status: ${response.status}`;

          try {
            const errorData = await response.json();
            if (errorData.detail) {
              errorMessage =
                typeof errorData.detail === "string"
                  ? errorData.detail
                  : JSON.stringify(errorData.detail);
            }
          } catch (error) {
            // Response wasn't JSON.
          }

          throw new Error(errorMessage);
        }
        const result = await response.json();

        console.log("Assessment saved:", result);

        this.savedAssessmentState = JSON.stringify(assessmentData);
        if (this.assessedSubjectIds && !this.isAssessed(this.currentSubjectId)) {
          this.assessedSubjectIds.push(this.currentSubjectId);
        }
        this.saved = true;

        return true;
      } catch (error) {
        console.error("Assessment save error:", error);

        this.errorMessage = error.message || "Unable to save assessment.";

        return false;
      } finally {
        this.saving = false;
      }
    },
    async nextSubject() {
      this.errorMessage = "";
      // Skip the request only when this case is already saved and unchanged
      if (this.hasUnsavedChanges() || !this.isAssessed(this.currentSubjectId)) {
        const saved = await this.saveAssessment();
        if (!saved) {
          return;
        }
      }
      if (this.currentSubjectIndex >= this.subjectIds.length - 1) {
        console.log("Last case completed.");
        return;
      }
      this.currentSubjectIndex += 1;
      this.currentSubjectId = this.subjectIds[this.currentSubjectIndex];

      await this.loadSubject(this.currentSubjectId);
    },
    async previousSubject() {
      if (this.currentSubjectIndex <= 0) {
        return;
      }

      this.errorMessage = "";
      // Save before going back so a changed selection isn't lost.
      // Unchanged cases are not re-sent.
      if (this.hasUnsavedChanges()) {
        const saved = await this.saveAssessment();
        if (!saved) {
          return;
        }
      }
      this.currentSubjectIndex -= 1;
      this.currentSubjectId = this.subjectIds[this.currentSubjectIndex];

      await this.loadSubject(this.currentSubjectId);
    },

    removeCurrentModel() {
      if (!this.model) {
        return;
      }

      this.model.traverse((child) => {
        if (!child.isMesh) {
          return;
        }

        if (child.geometry) {
          child.geometry.dispose();
        }

        if (child.material) {
          if (child.material.map) {
            child.material.map.dispose();
          }

          child.material.dispose();
        }
      });

      if (this.scene) {
        this.scene.remove(this.model);
      }

      this.model = null;
    },

    revokeBlobUrls() {
      if (this.textureBlobUrl) {
        URL.revokeObjectURL(this.textureBlobUrl);

        this.textureBlobUrl = null;
      }

      if (this.modelBlobUrl) {
        URL.revokeObjectURL(this.modelBlobUrl);

        this.modelBlobUrl = null;
      }
    },

    destroyThreeViewer() {
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);

        this.animationFrameId = null;
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();

        this.resizeObserver = null;
      }

      this.removeCurrentModel();

      if (this.controls) {
        this.controls.dispose();
      }

      if (this.renderer) {
        this.renderer.dispose();

        const canvas = this.renderer.domElement;

        if (canvas && canvas.parentNode) {
          canvas.parentNode.removeChild(canvas);
        }
      }

      this.scene = null;
      this.camera = null;
      this.renderer = null;
      this.controls = null;
    },
  },
};
</script>
<style scoped>
.clinical-assessment-page {
  width: auto;
  margin-left: 280px;
  margin-right: 72px;
  padding: 32px 0 48px;
  box-sizing: border-box;
  color: #263238;
}

.completion-message {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 24px;
  padding: 18px 20px;
  border: 1px solid #a8ddbf;
  border-radius: 10px;
  background: #f0faf4;
  color: #286644;
  font-size: 13px;
}

.completion-content {
  display: flex;
  align-items: center;
  gap: 13px;
}

.completion-icon {
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 50%;
  background: #d9f2e4;
  color: #258653;
  font-size: 16px;
  font-weight: 700;
}

.completion-copy {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.completion-copy strong {
  color: #245f3e;
  font-size: 14px;
  font-weight: 650;
}

.completion-copy span {
  color: #527362;
  font-size: 12px;
}

.review-button {
  padding: 9px 15px;
  border: 1px solid #55b983;
  border-radius: 7px;
  background: #fff;
  color: #32825a;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.review-button:hover {
  background: #e7f6ed;
  border-color: #399966;
}

.assessment-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 22px;
}

.header-copy h1 {
  margin: 0 0 9px;
  font-size: 24px;
  line-height: 1.2;
  font-weight: 700;
  letter-spacing: -0.3px;
  color: #263238;
}

.case-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 15px;
  color: #69757d;
}

.case-number {
  font-weight: 600;
  color: #45525a;
}

.ethnicity-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 9px;
  margin-left: 3px;
  border-radius: 20px;
  background: #eef3f5;
  color: #4b5961;
  font-size: 11px;
  font-weight: 600;
}

.progress-badge {
  display: flex;
  align-items: baseline;
  gap: 4px;
  min-width: 90px;
  padding: 9px 13px;
  border: 1px solid #dce2e6;
  border-radius: 8px;
  background: #fff;
  color: #5c6870;
  font-size: 13px;
  margin-top: 25px;
}

.progress-badge strong {
  color: #263238;
  font-size: 15px;
}

.progress-label {
  margin-right: 4px;
  color: #8b969d;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.7px;
}
.assessment-workspace {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.viewer-card {
  display: grid;
  grid-template-columns:
    minmax(500px, 1fr)
    330px;
  height: 540px;
  overflow: hidden;
  border: 1px solid #dce2e6;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(30, 42, 50, 0.04),
    0 5px 18px rgba(30, 42, 50, 0.04);
}
.viewer-panel {
  display: flex;
  flex-direction: column;
  min-width: 0;
  border-right: 1px solid #e1e6e9;
}

.panel-heading {
  height: 68px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 0 20px;
  box-sizing: border-box;
  border-bottom: 1px solid #e7ebee;
  background: #fff;
}

.panel-heading h2,
.reference-header h2,
.scoring-header h2 {
  margin: 2px 0 0;
  color: #023f89;
  font-size: 15px;
  font-weight: 650;
}

.panel-eyebrow,
.section-eyebrow {
  color: #023f89a0;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 1px;
}

.viewer-instruction {
  color: #023f8991;
  font-size: 11px;
}

.viewer-stage {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  background: #f5f7f8;
}

.three-viewer {
  width: 100%;
  height: 100%;
}

.viewer-loading {
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: rgba(245, 247, 248, 0.88);
  color: #69757c;
  font-size: 12px;
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #dde3e6;
  border-top-color: #596970;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.reference-panel {
  display: flex;
  flex-direction: column;
  width: 350px;
  min-width: 350px;
  background: #fbfcfc;
}

.reference-header {
  height: 92px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border-bottom: 1px solid #e7ebee;
  text-align: center;
}

.reference-ethnicity {
  display: inline-flex;
  margin-top: 6px;
  padding: 3px 8px;
  border-radius: 12px;
  background: #eef3f5;
  color: #023f89c5;
  font-size: 10px;
  font-weight: 600;
}
.reference-guide-selector {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 7px;
  padding: 3px;
  border-radius: 8px;
  background: #eef3f5;
}

.reference-guide-button {
  padding: 4px 8px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #69757d;
  font-family: inherit;
  font-size: 9px;
  font-weight: 600;
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    box-shadow 0.15s ease;
}

.reference-guide-button:hover {
  color: #023f89;
}

.reference-guide-button.active {
  background: #ffffff;
  color: #023f89;
  box-shadow: 0 1px 3px rgba(30, 42, 50, 0.12);
}

.reference-body {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0;
  padding: 16px;
  box-sizing: border-box;
}

.reference-image-frame {
  width: 270px;
  height: 405px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  box-sizing: border-box;
}

.reference-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  margin: 0;
  padding: 0;
}

.reference-unavailable {
  width: 270px;
  height: 405px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 1px dashed #ccd4d9;
  border-radius: 8px;
  color: #879299;
  text-align: center;
  font-size: 12px;
}

.reference-placeholder-icon {
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #eef1f3;
  font-weight: 700;
}

.scoring-card {
  padding: 22px 24px 24px;
  border: 1px solid #dce2e6;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(30, 42, 50, 0.03);
}

.scoring-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 20px;
}

.scoring-header h2 {
  font-size: 17px;
}

.scoring-help {
  padding-top: 3px;
  color: #8b969c;
  font-size: 11px;
}

.score-row {
  display: grid;
  grid-template-columns:
    165px
    minmax(400px, 1fr)
    145px;
  align-items: center;
  gap: 22px;
  min-height: 64px;
}

.score-description {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.score-title {
  color: #35434b;
  font-size: 14px;
  font-weight: 650;
}

.score-subtitle {
  color: #98a1a7;
  font-size: 10px;
}

.score-divider {
  height: 1px;
  margin: 10px 0;
  background: #edf0f2;
}

.back-button {
  flex-shrink: 0;
  border: 1px solid #d4dbdf;
  background: #fff;
  color: #536069;
}

.next-button {
  flex-shrink: 0;
  min-width: 160px;
  border: 1px solid #004ead !important;
  border-radius: 8px;
  background: #004ead;
  color: #ffffff;
  box-shadow: 0 3px 8px rgba(0, 78, 173, 0.16);
}

.score-buttons {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 7px;
  padding: 5px;
  border-radius: 9px;
  background: #f2f5f6;
  transition: opacity 0.2s ease;
}

.score-buttons.disabled {
  opacity: 0.42;
}

.score-button {
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid transparent;
  border-radius: 7px;
  background: #fff;
  color: #536069;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(31, 45, 53, 0.05);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease,
    transform 0.1s ease, box-shadow 0.15s ease;
}

.score-button:hover:not(:disabled) {
  border-color: #5fd797;
  background: #5fd797;
  transform: translateY(-1px);
}

.score-button.selected {
  border: none;
  outline: none;
  border-color: #5dca90;
  background: #5dca90;
  color: #092014;
  box-shadow: 0 3px 8px rgba(65, 151, 107, 0.18);
}

.score-number {
  font-size: 17px;
  font-weight: 650;
}

.score-button:disabled {
  cursor: not-allowed;
}

.na-control {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 8px 9px;
  border: 1px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.na-control:hover {
  background: #f6f8f9;
}

.na-control.active {
  border-color: #d5dcdf;
  background: #f2f5f6;
}

.na-control input {
  position: absolute;
  opacity: 0;
  pointer-events: none;
}

.custom-checkbox {
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border: 1.5px solid #aeb8bd;
  border-radius: 6px;
  background: #fff;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.na-control.active .custom-checkbox {
  border-color: #5dca90;
  background: #5dca90;
}

.na-copy {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.na-copy strong {
  color: #536069;
  font-size: 11px;
  font-weight: 600;
}

.na-copy small {
  margin-top: 2px;
  color: #98a2a8;
  font-size: 9px;
}

.comments-section {
  margin-top: 22px;
  padding-top: 20px;
  border-top: 1px solid #edf0f2;
}

.comments-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.comments-heading label {
  color: #3d4a52;
  font-size: 13px;
  font-weight: 650;
}

.comments-heading span {
  color: #9aa3a8;
  font-size: 10px;
}

.comments-section textarea {
  width: 100%;
  min-height: 78px;
  padding: 11px 13px;
  box-sizing: border-box;
  resize: vertical;
  border: 1px solid #d6dde1;
  border-radius: 8px;
  outline: none;
  background: #fbfcfc;
  color: #36434b;
  font-family: inherit;
  font-size: 12px;
  line-height: 1.5;
  transition: border-color 0.15s ease, background 0.15s ease,
    box-shadow 0.15s ease;
}

.comments-section textarea:focus {
  border-color: #93b7a6;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(85, 185, 131, 0.08);
}

.comments-section textarea::placeholder {
  color: #a6afb4;
}

.assessment-footer {
  display: grid;
  grid-template-columns:
    1fr
    auto
    1fr;
  align-items: center;
  min-height: 58px;
  padding-top: 2px;
}

.navigation-button {
  height: 50px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  padding: 0 17px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 12px;
  font-weight: 650;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease,
    transform 0.1s ease, opacity 0.15s ease;
}
.back-button:hover:not(:disabled) {
  background: #f5f7f8;
  border-color: #c4cdd2;
}
.score-button:focus,
.score-button:active {
  outline: none;
  border-color: transparent;
}
.next-button:hover:not(:disabled) {
  background: #004eadc2;
  transform: translateY(-1px);
}

.nav-arrow {
  font-size: 18px;
  line-height: 1;
}

.navigation-button:disabled {
  opacity: 0.35;
  cursor: not-allowed;
  transform: none;
}

.save-state {
  min-width: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.saving-status,
.saved-status {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: #718087;
  font-size: 11px;
}

.saved-status {
  color: #438262;
}

.saved-check {
  width: 19px;
  height: 19px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #e5f4ec;
  color: #438262;
  font-size: 11px;
  font-weight: 700;
}

.mini-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid #dce2e5;
  border-top-color: #65757d;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

.error-message {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
  padding: 11px 13px;
  border: 1px solid #ebc8c8;
  border-radius: 8px;
  background: #fff7f7;
  color: #865555;
  font-size: 12px;
}

.error-content {
  display: flex;
  align-items: center;
  gap: 9px;
}

.error-icon {
  width: 20px;
  height: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #f2dcdc;
  font-weight: 700;
}

.retry-button {
  padding: 6px 11px;
  border: 1px solid #d6b5b5;
  border-radius: 6px;
  background: #fff;
  color: #765050;
  cursor: pointer;
  font-size: 11px;
}

@media (max-width: 1200px) {
  .clinical-assessment-page {
    margin-left: 240px;
    margin-right: 32px;
  }

  .viewer-card {
    grid-template-columns:
      minmax(450px, 1fr)
      380px;
  }

  .score-row {
    grid-template-columns:
      135px
      minmax(320px, 1fr)
      135px;
    gap: 14px;
  }
}

@media (max-width: 900px) {
  .clinical-assessment-page {
    margin: 0;
    padding: 20px;
  }

  .assessment-header {
    align-items: flex-start;
  }

  .viewer-card {
    display: flex;
    flex-direction: column;
    height: auto;
  }

  .viewer-panel {
    height: 480px;
    border-right: none;
    border-bottom: 1px solid #e1e6e9;
  }

  .reference-panel {
    width: 100%;
    height: 520px;
  }

  .reference-image-frame {
    width: 300px;
    height: 405px;
  }

  .score-row {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 8px 0;
  }

  .score-description {
    flex-direction: row;
    align-items: baseline;
    justify-content: space-between;
  }

  .na-control {
    width: fit-content;
  }
}

@media (max-width: 520px) {
  .clinical-assessment-page {
    padding: 14px;
  }

  .assessment-header {
    flex-direction: column;
  }

  .viewer-panel {
    height: 390px;
  }

  .reference-image-frame {
    width: min(270px, 100%);
    height: 390px;
  }

  .score-buttons {
    gap: 4px;
  }

  .score-button {
    height: 42px;
  }

  .assessment-footer {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-top: 22px;
    padding-top: 20px;
    border-top: 1px solid #edf0f2;
    box-sizing: border-box;
  }

  .assessment-footer {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-top: 22px;
    padding-top: 20px;
    border-top: 1px solid #edf0f2;
    box-sizing: border-box;
  }
}
</style>